// Libraries
import { Box, Button, Flex, styled } from "@chakra-ui/react";
// @ts-ignore
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { animated, to as interpolate, useSprings } from "react-spring";
import { useDrag } from "react-use-gesture";
// import { HiChevronLeft, HiChevronRight, HiOutlineRefresh } from 'react-icons/hi';
import { useSetRecoilState } from "recoil";
import { currentCardIndex } from "../../State";
// Components
import TextIcon from "./TextIcon";

dayjs.extend(relativeTime);

const Card = styled(animated.div);
//   (
//   css({
//     position: "absolute",
//     width: "100%",
//     cursor: "grab",
//     paddingX: [4, 0],
//     userSelect: "none",
//     willChange: "transform",
//     zIndex: 400,
//   })
// );
const Wrapper = animated(Box);
/**
 * https://github.com/naizen/foodmatcher/blob/master/src/components/Deck.js
 */
// These two are just helpers, they curate spring data, values that are later being interpolated into css
const to = (i: number, current?: any) => ({
  x: 0,
  y: -1,
  // y: i * -4,
  scale: 1,
  rot: -5 + Math.random() * 10,
  delay: i * 40,
  shadow: i === current ? 1 : 0,
});
const from = (i: number) => ({ x: 0, rot: 0, scale: 1.1, y: -200, shadow: 0 });
// This is being used down there in the view, it interpolates rotation and scale into a css transform
const trans = (r: number, s: number) => `perspective(1500px) rotateX(0deg) rotateY(${r / 2}deg) rotateZ(${r}deg) scale(${s})`;

const SwipeCards: React.FC<{ data: any; renderItem: any; height?: any }> = ({ data, renderItem, height = "75vh" }: any) => {
  // recoil
  const setCurrentCardIndex = useSetRecoilState(currentCardIndex);

  // router
  const router = useRouter();

  // The set flags all the cards that are flicked out
  const [gone] = useState<any>(() => new Set());
  const [current, setCurrent] = useState<any>(data.length - 1);
  const [props, set] = useSprings(data.length, (i) => ({
    ...to(i, current),
    from: from(i),
  })); // Create a bunch of springs using the helpers above

  useEffect(() => {
    if (router.pathname === "/classes/enrolled") {
      setCurrentCardIndex(data.length - 1);
    }
  }, []);

  // This is to reset the cards back to the first
  const reset = () => {
    setCurrent(data.length - 1);
    if (router.pathname === "/classes/enrolled") {
      setCurrentCardIndex(data.length - 1);
    }
    setTimeout(() => gone.clear() || set((i) => to(i, data.length - 1)), 100);
  };

  // Create a gesture, we're interested in down-state, delta (current-pos - click-pos), direction and velocity
  const bind = useDrag(({ args: [index], down, movement: [mx], distance, direction: [xDir], velocity }) => {
    // If you flick hard enough it should trigger the card to fly out
    const trigger = velocity > 0.2;

    // Direction should either point left or right
    const dir = xDir < 0 ? -1 : 1;

    // If button/finger's up and trigger velocity is reached, we flag the card ready to fly out
    if (!down && trigger) {
      gone.add(index);
      //* Swipe cards function -> we can do a tinder-like-ish feature here. ATM: we're gonna use this to set the current card
      setCurrent(current - 1);
      if (router.pathname === "/classes/enrolled") {
        setCurrentCardIndex(current - 1);
      }
      // @ts-ignore
      set((i: any) => {
        if (current - 1 !== i) return;
        return {
          shadow: 1,
        };
      });
    }

    // @ts-ignore
    set((i: any) => {
      // We're only interested in changing spring-data for the current spring
      if (index !== i) return;
      const isGone = gone.has(index);
      const x = isGone ? (200 + window.innerWidth) * dir : down ? mx : 0; // When a card is gone it flys out left or right, otherwise goes back to zero
      const rot = mx / 100 + (isGone ? dir * 10 * velocity : 0); // How much the card tilts, flicking it harder makes it rotate faster
      const scale = down ? 1.01 : 1; // Active cards lift up a bit

      // if (router.pathname === "/classes/enrolled") {
      //   setCurrentCardIndex(index);
      // }

      return {
        x,
        rot,
        scale,
        delay: undefined,
        config: {
          mass: 3,
          friction: 50,
          tension: down ? 800 : isGone ? 200 : 500,
        },
      };
    });
    if (!down && gone.size === data.length) {
      reset();
    }
  });

  return (
    <React.Fragment>
      <Flex
        touchaction="pan-y"
        width="100%"
        pt={{ base: 2, md: 0 }}
        position="relative"
        minHeight={{ base: "60vh", lg: "70vh" }}
        id="cynCard">
        {props.map(({ x, y, rot, scale }, i) => (
          <Card
            position="absolute"
            width="100%"
            cursor="grab"
            paddingX={[4, 0]}
            userSelect="none"
            willChange="transform"
            zIndex={10}
            key={i}
            style={{ x, y }}>
            <Wrapper
              color="gray.500"
              borderRadius="1rem"
              // @ts-ignore
              transition="all .3s ease"
              touchaction="none"
              display="flex"
              flexDirection="column"
              alignContent="flex-start"
              background="white"
              willChange="auto"
              overflow="hidden"
              boxShadow={current === i ? `0 0 1px 1px #00000020, 0 10px 10px -2.5px #00000020` : "0 0 0 1px #00000020"}
              h={{ base: "60vh", lg: "70vh" }}
              position="relative"
              _hover={{
                bg: "gray.50",
              }}
              {...bind(i)}
              // @ts-ignore
              style={{
                transform:
                  current !== i
                    ? interpolate([rot, scale], trans)
                    : "perspective(1500px) rotateX(0deg) rotateY(0deg) rotateZ(0deg) scale(1.025)",
              }}>
              {renderItem(data[i])}
            </Wrapper>
          </Card>
        ))}
        <Button
          variant="ghost"
          colorScheme="blue"
          color="blue.500"
          bg="white"
          size="lg"
          boxShadow="0 0 0 1px #00000020, 0 1px 1px 3px hsla(0,0%,0%,0.015)"
          borderRadius="50%"
          pos="absolute"
          left="50%"
          bottom={{ base: "-0.1rem", md: "-2.5rem" }}
          transform="translateX(-50%)"
          zIndex={1200}
          d={{ base: "none", md: "block" }}
          height="unset"
          p={4}
          id="cynResetCards"
          onClick={reset}>
          <TextIcon fontSize="xl" m="auto">
            <use xlinkHref="../../../symbols.svg#refresh"></use>
          </TextIcon>
        </Button>
      </Flex>
    </React.Fragment>
  );
};

export default SwipeCards;
